import React from "react";
import { Mail, Phone, Stethoscope, ShieldCheck, Pill, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

const ROLES = [
  { icon: ShieldCheck, role: "Admin",   email: "admin@chemo.app",   pw: "Admin@123",   blurb: "Manages providers, organization settings, role assignments, and the regimen library. Full access across the platform." },
  { icon: Stethoscope, role: "Doctor",  email: "doctor@chemo.app",  pw: "Doctor@123",  blurb: "Composes regimens, generates patient calendars, signs off on reschedule requests, and reviews ChemoAnalytics." },
  { icon: Pill,        role: "Nurse",   email: "nurse@chemo.app",   pw: "Nurse@123",   blurb: "Administers medications, logs side-effects, manages the daily roster, and responds to patient messages." },
  { icon: Smartphone,  role: "Patient", email: "patient@chemo.app", pw: "Patient@123", blurb: "Mobile PWA — sees appointments, medications, side-effect log, messages the care team, configures SMS / email reminders." },
];

export default function ContactPage() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-16 pb-12 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Contact</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">Two ways to reach us. Four ways to explore the platform.</h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Email or call — we respond within one business day. Or skip the chat and log in directly with any of the four demo accounts below.</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-16 grid md:grid-cols-2 gap-5">
        <a href="mailto:DEMO@BioMedMeet.com" className="glass-card glass-card-hover p-7 block" data-testid="contact-email">
          <div className="w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
            <Mail className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1">Email</div>
          <div className="text-xl font-bold text-emerald-400">DEMO@BioMedMeet.com</div>
          <p className="text-sm text-slate-400 mt-2">Best for: demo requests, pricing for multi-site networks, IT/security questionnaires, BAA &amp; HIPAA documentation.</p>
        </a>
        <a href="tel:+14848026153" className="glass-card glass-card-hover p-7 block" data-testid="contact-phone">
          <div className="w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
            <Phone className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1">Phone (text or call)</div>
          <div className="text-xl font-bold text-emerald-400">+1 (484) 802-6153</div>
          <p className="text-sm text-slate-400 mt-2">Best for: same-day demo scheduling, customer questions, urgent deployment support during onboarding. 9am-7pm ET.</p>
        </a>
      </section>

      <section className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 pb-16">
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Try every role yourself</div>
          <h2 className="text-3xl font-bold">Four demo accounts. One live environment.</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">Click any card to sign in directly. Same data set, same calendars, same patient records — see the platform from the perspective of every user role.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROLES.map((r, i) => (
            <div key={r.role} className="glass-card glass-card-hover p-6 fade-up" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`contact-role-${r.role.toLowerCase()}`}>
              <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                <r.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-1">{r.role}</div>
              <div className="text-xs font-mono text-slate-300 break-all">{r.email}</div>
              <div className="text-[11px] font-mono text-slate-500 mt-0.5">{r.pw}</div>
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">{r.blurb}</p>
              <Link to="/login" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-400 hover:text-emerald-300 mt-3">
                Open the login page →
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="glass-card p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold">Prefer a structured walk-through?</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">Tell us a bit about your practice and we&apos;ll prepare a 30-minute demo tailored to your team&apos;s workflow.</p>
          <Link to="/demo" className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors">
            Request a demo
          </Link>
        </div>
      </section>
    </div>
  );
}
